import * as React from 'react'
import clsx from 'clsx'
import { ButtonBase, ButtonBaseProps } from '../ButtonBase'
import { capitalize } from '../../utils'
import style from './index.module.scss'

export interface IconButtonClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** State class applied to the root element if `selected={true}`. */
  selected: string
}

export interface IconButtonProps extends ButtonBaseProps {
  /**
   * The icon to display.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<IconButtonClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * Sets the border radius - rounded, suare, or circle
   * @default 'rounded'
   */
  borderRadius?: 'rounded' | 'square' | 'circle'
  /**
   * If `true`, the  keyboard focus ripple is disabled.
   * @default false
   */
  disableFocusRipple?: boolean
  /**
   * If given, uses a negative margin to counteract the padding on one
   * side (this is often helpful for aligning the left or right
   * side of the icon with content above or below, without ruining the border
   * size and shape).
   * @default false
   */
  edge?: 'start' | 'end' | false
  /**
   * The size of the component.
   * `small` is equivalent to the dense button styling.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * If `true`, the button is rendered in an active state.
   */
  selected?: boolean
}

/**
 * Refer to the [Icons](/material-ui/icons/) section of the documentation
 * regarding the available icon options.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(inProps, ref) {
    const {
      edge = false,
      children,
      className,
      color = 'primary',
      borderRadius = 'rounded',
      disabled = false,
      disableFocusRipple = true,
      size = 'medium',
      selected,
      classes,
      ...other
    } = inProps

    const _className = clsx(
      [style.root, classes?.root],
      disabled && [style.disable, classes?.disabled],
      // @ts-ignore
      edge && style[`edge${capitalize(edge)}--${size}`],
      // @ts-ignore
      color && style[`color--${color}`],
      selected && [style.active, classes?.selected],
      borderRadius && style[`borderRadius--${borderRadius}`],
      style[`size--${size}`],
      className
    )

    return (
      <ButtonBase
        className={_className}
        centerRipple
        focusRipple={!disableFocusRipple}
        disabled={disabled}
        ref={ref}
        {...other}
      >
        {children}
      </ButtonBase>
    )
  }
)
